"use client";

import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  ChevronRight,

  LineChart,
  ArrowLeftRight,
  History,
  PieChart,
  BarChart3,
  Search,
  Users,
  Coins,
  GraduationCap,
  TrendingUp,
  Layers,
  Activity,
  Cpu,
  BookOpen,
  type LucideIcon,
} from "lucide-react";
import Link from "next/link";

// Custom Xynth Curated Icon - Sophisticated/Scientific/Wealthy
const XynthIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    className={className}
  >
    <path d="M12 3L14.5 7.5H9.5L12 3Z" fill="currentColor" stroke="none" />
    <path d="M19 15L21.5 19.5H16.5L19 15Z" fill="currentColor" stroke="none" />
    <path d="M5 15L7.5 19.5H2.5L5 15Z" fill="currentColor" stroke="none" />
    <path d="M12 7.5V12" strokeLinecap="round" />
    <path d="M12 12L16.5 15" strokeLinecap="round" />
    <path d="M12 12L7.5 15" strokeLinecap="round" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

export const CATEGORIES = [
  { name: "Stock Analysis", icon: LineChart },
  { name: "Options Trading", icon: ArrowLeftRight },
  { name: "Backtesting", icon: History },
  { name: "Portfolio Strategy", icon: PieChart },
  { name: "Technical Analysis", icon: BarChart3 },
  { name: "Market Research", icon: Search },
  { name: "Crypto Analysis", icon: Coins },
];

const DIFFICULTY_PILLS: { name: string; value: string; icon: LucideIcon }[] = [
  { name: "Beginner", value: "beginner", icon: BookOpen },
  { name: "Intermediate", value: "intermediate", icon: TrendingUp },
  { name: "Advanced", value: "advanced", icon: Activity },
  { name: "PhD", value: "phd", icon: Cpu },
];

const THEME_PILLS: { name: string; value: string; icon: LucideIcon }[] = [
  { name: "Stocks", value: "stocks", icon: LineChart },
  { name: "Options", value: "options", icon: ArrowLeftRight },
  { name: "Flow", value: "flow_analysis", icon: Layers },
  { name: "Fundamentals", value: "fundamentals", icon: BarChart3 },
  { name: "Screener", value: "screener", icon: Search },
  { name: "Quant", value: "quant", icon: GraduationCap },
];

export type ShowcaseTab =
  | "skills"
  | "signals"
  | "curated"
  | "community"
  | "automations"
  | "watch"
  | "feedback";

/**
 * Tabs rendered in the landing-page showcase strip. Order mirrors the IA:
 * Skills · Signals · Automations · Blogs · Community. Skills + Signals link
 * to the Library sections.
 */
const SHOWCASE_TABS: {
  key: ShowcaseTab;
  label: string;
  href: string;
  isNew?: boolean;
  // Persistent emerald accent (the "go here to learn" pill) — used by
  // Tutorials so new users can always find the onboarding video again.
  highlight?: boolean;
  // When true the pill navigates straight to `href` instead of switching the
  // showcase content in place (used for surfaces that have their own page).
  directLink?: boolean;
}[] = [
  { key: "skills", label: "Skills", href: "/library" },
  { key: "signals", label: "Signals", href: "/library" },
  { key: "automations", label: "Automations", href: "/automations" },
  { key: "curated", label: "User Stories", href: "/blog" },
  { key: "watch", label: "Tutorials", href: "/library?tab=video", highlight: true },
  { key: "community", label: "Community Use Cases", href: "/community" },
  { key: "feedback", label: "Feedback", href: "/feedback", isNew: true, directLink: true },
];

interface FeedHeaderProps {
  totalUsers: number | null;
  stickyHeader?: boolean;
  variant?: "teaser" | "full";
  sortBy: "relevance" | "recent";
  onSortChange: (sort: "relevance" | "recent") => void;
  selectedCategory: string | null;
  onCategoryChange: (category: string | null) => void;
  activeTab?: ShowcaseTab;
  onTabChange?: (tab: ShowcaseTab) => void;
  tourActiveTab?: ShowcaseTab | null;
}

function AnimatedTraderCount({ totalUsers }: { totalUsers: number | null }) {
  const [display, setDisplay] = useState<number | null>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (totalUsers == null) return;
    if (hasAnimated.current) {
      setDisplay(totalUsers);
      return;
    }
    hasAnimated.current = true;

    const duration = 2500;
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // fast start, smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 4);
      setDisplay(Math.round(eased * totalUsers));
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [totalUsers]);

  if (display == null) return null;

  return (
    <div className="hidden sm:flex items-center gap-2.5">
      <div className="relative flex h-2.5 w-2.5">
        <span
          className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"
          style={{ animationDuration: "1.5s" }}
        ></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400"></span>
      </div>
      <span className="text-base text-zinc-400">
        Used by{" "}
        <span className="font-semibold text-white">
          {display.toLocaleString()}
        </span>{" "}
        traders
      </span>
    </div>
  );
}

export function FeedHeader({
  totalUsers,
  stickyHeader = false,
  variant = "teaser",
  sortBy,
  onSortChange,
  selectedCategory,
  onCategoryChange,
  activeTab = "curated",
  onTabChange,
  tourActiveTab = null,
}: FeedHeaderProps) {
  const getSortLabel = () => {
    if (sortBy === "recent") return "Trending";
    return "Featured";
  };

  return (
    <div
      className={cn(
        "mb-3 w-full min-w-0",
        stickyHeader &&
          "bg-background py-4 md:-mx-4 px-4 border-b border-border"
      )}
    >
      {/* Header Block */}

      {variant === "teaser" ? (
        /* ── Teaser mode: pills + traders count ──
           On mobile the three pill labels (Automations / Top Trade Ideas /
           Community Use Cases) combine to ~510px which is wider than a
           phone viewport. We let the pill row scroll horizontally inside
           its own container rather than push the whole page sideways.
           `min-w-0` on both flex parents is the key — without it, the
           nowrap children would force the outer column wider than the
           screen and cause page-level horizontal scroll. */
        <div className="flex items-center justify-between gap-3 w-full min-w-0">
          <div
            data-tour="home-showcase-tabs"
            className="flex items-center gap-2 min-w-0 overflow-x-auto no-scrollbar -mx-1 px-1"
          >
            {SHOWCASE_TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              const isNew = Boolean(tab.isNew);
              const isHighlight = Boolean(tab.highlight);
              const isDirectLink = Boolean(tab.directLink);
              return (
                <Link
                  key={tab.key}
                  data-tour={
                    tab.key === "automations"
                      ? "home-automations-pill"
                      : tab.key === "curated"
                        ? "home-trade-ideas-pill"
                        : tab.key === "community"
                          ? "home-community-pill"
                          : undefined
                  }
                  data-tour-tab={tab.key === "curated" ? "trade-ideas" : tab.key}
                  href={isDirectLink || isActive ? tab.href : "#"}
                  onClick={(e) => {
                    if (isDirectLink) return;
                    if (!isActive) {
                      e.preventDefault();
                      onTabChange?.(tab.key);
                    }
                  }}
                  className={cn(
                    "h-9 sm:h-10 px-4 sm:px-5 text-sm sm:text-[15px] font-semibold rounded-full border transition-all duration-200 whitespace-nowrap inline-flex items-center gap-2 relative shrink-0",
                    isActive
                      ? isNew
                        ? "bg-white text-black border-cyan-400/50 shadow-[0_0_18px_-6px_rgba(34,211,238,0.6)]"
                        : isHighlight
                          ? "bg-white text-black border-emerald-400/60 shadow-[0_0_20px_-6px_rgba(16,185,129,0.7)]"
                          : "bg-white text-black border-white shadow-[0_0_22px_-6px_rgba(16,185,129,0.55)]"
                      : isNew
                        ? "bg-cyan-400/[0.06] text-cyan-200/90 border-cyan-300/30 hover:text-cyan-100 hover:border-cyan-200/55 hover:bg-cyan-400/[0.12] shadow-[0_0_18px_-6px_rgba(34,211,238,0.5)]"
                        : isHighlight
                          ? "bg-emerald-400/[0.07] text-emerald-200/95 border-emerald-300/35 hover:text-emerald-100 hover:border-emerald-200/60 hover:bg-emerald-400/[0.13] shadow-[0_0_18px_-6px_rgba(16,185,129,0.55)]"
                          : "bg-white/[0.04] text-zinc-300 border-white/15 hover:text-white hover:border-emerald-400/40 hover:bg-white/[0.08]"
                  )}
                >
                  {(isNew || isHighlight) && (
                    <span
                      aria-hidden
                      className={cn(
                        "inline-flex h-1.5 w-1.5 rounded-full",
                        isActive
                          ? "bg-black shadow-none"
                          : isNew
                            ? "bg-cyan-300 shadow-[0_0_6px_rgba(103,232,249,0.9)]"
                            : "bg-emerald-300 shadow-[0_0_6px_rgba(110,231,183,0.9)]",
                      )}
                    />
                  )}
                  {tab.label}
                </Link>
              );
            })}
          </div>

          <AnimatedTraderCount totalUsers={totalUsers} />
        </div>
      ) : (
        /* ── Full mode: keep existing sort + category filters ── */
        <div className="flex flex-col gap-3 w-full md:flex-row md:items-center min-w-0">
          {/* Sort Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="bg-zinc-900 border-zinc-800 text-zinc-300 min-w-[100px] h-8 text-sm justify-between hover:bg-zinc-800 hover:text-white shrink-0"
              >
                {getSortLabel()}
                <ChevronDown className="h-3.5 w-3.5 ml-2 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-[140px] bg-zinc-900 border-zinc-800 text-zinc-300"
            >
              <DropdownMenuItem
                onClick={() => onSortChange("relevance")}
                className="focus:bg-zinc-800 focus:text-white cursor-pointer"
              >
                Featured
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onSortChange("recent")}
                className="focus:bg-zinc-800 focus:text-white cursor-pointer"
              >
                Trending
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onSortChange("relevance")}
                className="focus:bg-zinc-800 focus:text-white cursor-pointer"
              >
                Popular
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Categories Scroll */}
          <div
            data-tour="community-filter-bar"
            className="w-full md:flex-1 overflow-hidden min-w-0"
          >
            <div className="w-full md:overflow-x-auto md:no-scrollbar md:mask-fade-right">
              <div className="flex flex-wrap gap-2 md:flex-nowrap md:min-w-max md:pr-4">
                {CATEGORIES.map((cat) => (
                  <Button
                    key={cat.name}
                    variant="outline"
                    size="sm"
                    data-tour={
                      cat.name === "Options Trading"
                        ? "community-filter-options"
                        : undefined
                    }
                    onClick={() =>
                      onCategoryChange(
                        selectedCategory === cat.name ? null : cat.name
                      )
                    }
                    className={cn(
                      "h-8 text-sm px-4 rounded-lg border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-white hover:border-zinc-700 whitespace-nowrap transition-colors",
                      selectedCategory === cat.name &&
                        "bg-zinc-100 text-black border-zinc-100 hover:bg-white hover:text-black hover:border-white"
                    )}
                  >
                    <cat.icon className="mr-1.5 h-3.5 w-3.5" />
                    {cat.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
